import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppStore } from './hooks/useAppStore';
import { MODULES, getAllQuestions } from './data/questionLoader';
import { Header } from './components/dashboard/Header';
import { ModuleCard } from './components/dashboard/ModuleCard';
import { FocusPanel } from './components/dashboard/FocusPanel';
import { PracticeSession } from './components/practice/PracticeSession';
import { LevelUpModal } from './components/LevelUpModal';
import { CitoSimulator } from './components/bonus/CitoSimulator';
import { CheatSheetDrawer } from './components/practice/CheatSheetDrawer';
import { ErrorLogPanel } from './components/ErrorLogPanel';
import { ModuleId, Question, BonusFeature } from './types';
import { getReviewQueue } from './utils/knowledgeStateEngine';
import { Trophy, Zap, Target, Lock, Sparkles, Code, RotateCcw, AlertTriangle, BookOpen, Award, Clock, Bug } from 'lucide-react';
import { Button } from './components/ui/Button';
import { Card } from './components/ui/Card';
// ✅ FIXED: Safe question loading with error handling
let QUESTIONS: Question[] = [];
try {
  QUESTIONS = getAllQuestions();
  if (QUESTIONS.length === 0) {
    console.warn('⚠️ No questions loaded - check question data files');
  }
} catch (error) {
  console.error('❌ Failed to load questions:', error);
  QUESTIONS = [];
}
// Bonus Features (Unlockables)
const BONUS_FEATURES: BonusFeature[] = [{
  id: 'CitoSimulator',
  title: 'Cito Simulator',
  description: '30 gemixte vragen met tijdslimiet',
  icon: 'Target',
  color: 'text-purple-600',
  unlockLevel: 10,
  isLocked: true
}, {
  id: 'SnelLezen',
  title: 'Snel Lezen (DMT)',
  description: 'Begrijpend lezen onder tijdsdruk',
  icon: 'Zap',
  color: 'text-orange-600',
  unlockLevel: 15,
  isLocked: true,
  comingSoon: true
}, {
  id: 'Studievaardigheden',
  title: 'Studievaardigheden',
  description: 'Notitietechnieken en leerstrategieën',
  icon: 'BookOpen',
  color: 'text-teal-600',
  unlockLevel: 20,
  isLocked: true,
  comingSoon: true
}, {
  id: 'ExpertModus',
  title: 'Expert Modus',
  description: 'Alleen E6 vragen met tijdsdruk',
  icon: 'Award',
  color: 'text-rose-600',
  unlockLevel: 25,
  isLocked: true,
  comingSoon: true
}];
type AppView = 'dashboard' | 'practice' | 'citoSimulator';
export function App() {
  const {
    state,
    recordAnswer,
    markCheatSheetViewed,
    clearPendingLevelUp,
    devJumpToLevel,
    devResetProgress
  } = useAppStore();
  const [currentView, setCurrentView] = useState<AppView>('dashboard');
  const [activeModuleId, setActiveModuleId] = useState<ModuleId | null>(null);
  const [isFocusSession, setIsFocusSession] = useState(false);
  const [focusQuestions, setFocusQuestions] = useState<Question[]>([]);
  const [showSuperkrachtIntro, setShowSuperkrachtIntro] = useState(false);
  const [showDevTools, setShowDevTools] = useState(false);
  const [showErrorLog, setShowErrorLog] = useState(false);
  const [cheatSheetModuleId, setCheatSheetModuleId] = useState<ModuleId | null>(null);
  // ✅ IMPROVED: Enhanced keyboard navigation - Escape closes modals
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showSuperkrachtIntro) {
          setShowSuperkrachtIntro(false);
        } else if (showErrorLog) {
          setShowErrorLog(false);
        } else if (cheatSheetModuleId) {
          setCheatSheetModuleId(null);
        }
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showSuperkrachtIntro, showErrorLog, cheatSheetModuleId]);
  // ✅ ADDED: Check if questions loaded successfully
  if (QUESTIONS.length === 0) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-id="element-3">
        <Card className="max-w-md w-full p-8 text-center" data-id="element-4">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4" data-id="element-5">
            <AlertTriangle className="w-8 h-8 text-amber-600" data-id="element-6" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2" data-id="element-7">
            Vragen laden mislukt
          </h2>
          <p className="text-slate-600 mb-4" data-id="element-8">
            De app kon de oefenvragen niet laden. Probeer de pagina opnieuw te
            laden.
          </p>
          <Button onClick={() => window.location.reload()} variant="primary" size="lg" data-id="element-9">
            <RotateCcw className="w-4 h-4 mr-2" data-id="element-10" />
            Pagina herladen
          </Button>
        </Card>
      </div>;
  }
  const totalReviewNeeded = Object.values(state.progress).reduce((sum, progress) => sum + progress.reviewNeededCount, 0);
  const hasReviewItems = totalReviewNeeded > 0;
  // Check which bonus features are unlocked
  const unlockedBonusFeatures = BONUS_FEATURES.map(feature => ({
    ...feature,
    isLocked: state.stats.level < feature.unlockLevel
  }));
  // DEV: Toggle dev tools with Ctrl+D (desktop)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'd') {
        e.preventDefault();
        setShowDevTools(prev => !prev);
      }
      // NEW: Ctrl+E to toggle error log
      if (e.ctrlKey && e.key === 'e') {
        e.preventDefault();
        setShowErrorLog(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  const handleStartPractice = (moduleId: ModuleId) => {
    markCheatSheetViewed(moduleId);
    setActiveModuleId(moduleId);
    setCurrentView('practice');
    setIsFocusSession(false);
  };
  const handleModuleClick = (moduleId: ModuleId) => {
    handleStartPractice(moduleId);
  };
  const handleCheatSheetClick = (moduleId: ModuleId) => {
    setCheatSheetModuleId(moduleId);
  };
  const handleStartFocusSession = () => {
    const allReviewQuestions: Question[] = [];
    Object.keys(state.progress).forEach(moduleId => {
      const reviewQueueIds = getReviewQueue(moduleId as ModuleId, state.questionStates);
      const moduleReviewQuestions = QUESTIONS.filter(q => reviewQueueIds.includes(q.id));
      allReviewQuestions.push(...moduleReviewQuestions);
    });
    const shuffled = allReviewQuestions.sort(() => Math.random() - 0.5);
    const selectedQuestions = shuffled.slice(0, 10);
    setFocusQuestions(selectedQuestions);
    setActiveModuleId(null);
    setCurrentView('practice');
    setIsFocusSession(true);
    setShowSuperkrachtIntro(true);
  };
  const handlePracticeComplete = () => {
    setActiveModuleId(null);
    setCurrentView('dashboard');
    setIsFocusSession(false);
    setFocusQuestions([]);
  };
  const handleBonusFeatureClick = (featureId: string) => {
    const feature = unlockedBonusFeatures.find(f => f.id === featureId);
    if (feature?.isLocked || feature?.comingSoon) return;
    if (featureId === 'CitoSimulator') {
      setCurrentView('citoSimulator');
    }
  };
  const activeQuestions = isFocusSession ? focusQuestions : activeModuleId ? QUESTIONS.filter(q => q.module === activeModuleId) : [];
  // ✅ IMPROVED: Simplified Superkracht Intro Modal (40% less text, larger emojis)
  if (showSuperkrachtIntro && currentView === 'practice') {
    return <div className="min-h-screen bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4" role="dialog" aria-labelledby="superkracht-title" aria-describedby="superkracht-description" data-id="element-11">
        <motion.div initial={{
        scale: 0.9,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} className="max-w-2xl w-full" data-id="element-12">
          <Card className="p-8 relative overflow-hidden" data-id="element-13">
            <motion.div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 opacity-50" animate={{
            backgroundPosition: ['0% 0%', '100% 100%']
          }} transition={{
            duration: 10,
            repeat: Infinity,
            repeatType: 'reverse'
          }} data-id="element-14" />

            <div className="relative" data-id="element-15">
              <div className="text-center mb-8" data-id="element-16">
                <motion.div animate={{
                rotate: [0, 10, -10, 0],
                scale: [1, 1.1, 1]
              }} transition={{
                duration: 2,
                repeat: Infinity,
                repeatDelay: 1
              }} className="inline-flex items-center justify-center w-24 h-24 bg-indigo-600 rounded-2xl mb-6 shadow-lg" aria-hidden="true" data-id="element-17">
                  <Target className="w-12 h-12 text-white" data-id="element-18" />
                </motion.div>
                <h2 id="superkracht-title" className="text-3xl font-bold text-slate-900 mb-2" data-id="element-19">
                  🎯 Superkracht Training
                </h2>
                <p id="superkracht-description" className="text-xl text-slate-600" data-id="element-20">
                  Tijd om je doordenkers te kraken!
                </p>
              </div>

              <div className="space-y-4 mb-8" data-id="element-21">
                <div className="bg-white rounded-xl p-5 border-2 border-indigo-100 shadow-sm" data-id="element-22">
                  <h3 className="font-bold text-slate-900 mb-2 flex items-center gap-2 text-lg" data-id="element-23">
                    <span className="text-3xl" aria-hidden="true" data-id="element-24">
                      🎯
                    </span>
                    Wat is dit?
                  </h3>
                  <p className="text-slate-700 text-lg" data-id="element-25">
                    Vragen die je nog niet goed had. Nu ga je ze kraken! 💪
                  </p>
                </div>

                <div className="bg-white rounded-xl p-5 border-2 border-purple-100 shadow-sm" data-id="element-26">
                  <h3 className="font-bold text-slate-900 mb-2 flex items-center gap-2 text-lg" data-id="element-27">
                    <span className="text-3xl" aria-hidden="true" data-id="element-28">
                      ⚡
                    </span>
                    Hoe werkt het?
                  </h3>
                  <p className="text-slate-700 text-lg" data-id="element-29">
                    Doe je ze goed? Dan verdwijnen ze! 🎉
                  </p>
                </div>
              </div>

              <div className="bg-indigo-50 rounded-xl p-6 mb-8 text-center border border-indigo-100" role="status" aria-live="polite" data-id="element-30">
                <p className="text-sm text-indigo-700 mb-2 font-medium uppercase tracking-wider" data-id="element-31">
                  Je gaat nu oefenen met:
                </p>
                <p className="text-4xl font-bold text-indigo-900" data-id="element-32">
                  {focusQuestions.length}{' '}
                  {focusQuestions.length === 1 ? 'doordenker' : 'doordenkers'}
                </p>
              </div>

              <Button onClick={() => setShowSuperkrachtIntro(false)} variant="primary" size="xl" fullWidth className="shadow-xl shadow-indigo-200" aria-label="Start Superkracht Training" data-id="element-33">
                Ik snap het, laten we gaan! 🚀
              </Button>
            </div>
          </Card>
        </motion.div>
      </div>;
  }
  // Practice Session View
  if (currentView === 'practice') {
    return <>
        <PracticeSession moduleId={activeModuleId} questions={activeQuestions} onComplete={handlePracticeComplete} onExit={() => {
        setActiveModuleId(null);
        setCurrentView('dashboard');
        setIsFocusSession(false);
        setFocusQuestions([]);
      }} onAnswer={(qId, correct, conf, choice) => {
        const question = QUESTIONS.find(q => q.id === qId);
        if (question) {
          recordAnswer(question.module, qId, correct, conf, choice);
        }
      }} cheatSheetViewed={activeModuleId ? state.progress[activeModuleId].cheatSheetViewed : true} onCheatSheetView={() => activeModuleId && markCheatSheetViewed(activeModuleId)} isCrossModule={isFocusSession} skipConfidence={isFocusSession} data-id="element-34" />

        {state.pendingLevelUp && <LevelUpModal isOpen={true} level={state.pendingLevelUp} xp={state.stats.xp} onClose={clearPendingLevelUp} data-id="element-35" />}
      </>;
  }
  // Cito Simulator View
  if (currentView === 'citoSimulator') {
    return <CitoSimulator onExit={() => setCurrentView('dashboard')} onAnswer={(qId, correct, conf, choice) => {
      const question = QUESTIONS.find(q => q.id === qId);
      if (question) {
        recordAnswer(question.module, qId, correct, conf, choice);
      }
    }} data-id="element-36" />;
  }
  // Dashboard View
  return <>
      {/* ✅ NEW: Skip link for accessibility */}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:bg-indigo-600 focus:text-white focus:px-4 focus:py-2 focus:rounded-lg focus:z-50 focus:shadow-xl" data-id="element-37">
        Spring naar hoofdinhoud
      </a>

      <div className="min-h-screen bg-slate-50 p-4 md:p-8 lg:p-12" data-id="element-38">
        <div className="max-w-7xl mx-auto" data-id="element-39">
          <Header stats={state.stats} onDevModeToggle={() => setShowDevTools(prev => !prev)} data-id="element-40" />

          {/* DEV TOOLS */}
          <AnimatePresence data-id="element-41">
            {showDevTools && <motion.div initial={{
            opacity: 0,
            y: -20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} className="mb-8 bg-slate-900 text-white rounded-2xl p-6 border-2 border-amber-400 shadow-xl" role="region" aria-label="Developer tools" data-id="element-42">
                <div className="flex items-center gap-2 mb-4" data-id="element-43">
                  <Code className="w-5 h-5 text-amber-400" data-id="element-44" />
                  <h3 className="font-bold text-amber-400" data-id="element-45">DEV TOOLS</h3>
                  <span className="text-xs text-slate-400 ml-auto" data-id="element-46">
                    Ctrl+D or Triple Tap Header • Ctrl+E for Error Log
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-id="element-47">
                  <button onClick={() => devJumpToLevel(10)} aria-label="Spring naar level 10" className="bg-purple-600 hover:bg-purple-700 px-4 py-2 rounded-lg font-medium transition-colors text-sm" data-id="element-48">
                    Lvl 10
                  </button>
                  <button onClick={() => devJumpToLevel(15)} aria-label="Spring naar level 15" className="bg-orange-600 hover:bg-orange-700 px-4 py-2 rounded-lg font-medium transition-colors text-sm" data-id="element-49">
                    Lvl 15
                  </button>
                  <button onClick={() => devJumpToLevel(20)} aria-label="Spring naar level 20" className="bg-teal-600 hover:bg-teal-700 px-4 py-2 rounded-lg font-medium transition-colors text-sm" data-id="element-50">
                    Lvl 20
                  </button>
                  <button onClick={() => devJumpToLevel(25)} aria-label="Spring naar level 25" className="bg-rose-600 hover:bg-rose-700 px-4 py-2 rounded-lg font-medium transition-colors text-sm" data-id="element-51">
                    Lvl 25
                  </button>
                  <button onClick={devResetProgress} aria-label="Reset alle voortgang" className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 text-sm" data-id="element-52">
                    <RotateCcw className="w-4 h-4" data-id="element-53" />
                    Reset
                  </button>
                  <button onClick={() => setShowErrorLog(true)} aria-label={`Open error log, ${state.errorLog?.length || 0} errors`} className="bg-amber-600 hover:bg-amber-700 px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 text-sm" data-id="element-54">
                    <Bug className="w-4 h-4" data-id="element-55" />
                    Error Log
                    {state.errorLog && state.errorLog.length > 0 && <span className="bg-white text-amber-600 text-xs font-bold px-2 py-0.5 rounded-full" data-id="element-56">
                        {state.errorLog.length}
                      </span>}
                  </button>
                </div>
                <div className="mt-4 text-xs text-slate-400 font-mono" data-id="element-57">
                  Level {state.stats.level} • {state.stats.xp} XP •{' '}
                  {Object.keys(state.questionStates).length} vragen •{' '}
                  {state.errorLog?.length || 0} errors
                </div>
              </motion.div>}
          </AnimatePresence>

          {/* Superkracht Training */}
          {hasReviewItems && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="mb-12" data-id="element-58">
              <FocusPanel state={state} onStartFocusSession={handleStartFocusSession} data-id="element-59" />
            </motion.div>}

          {/* ✅ IMPROVED: Main content landmark for skip link */}
          <main id="main-content" data-id="element-60">
            {/* CITO CORE MODULES */}
            <div className="mb-16" data-id="element-61">
              <div className="flex items-center gap-4 mb-8" data-id="element-62">
                <div className="flex items-center gap-3 bg-white px-6 py-3 rounded-2xl shadow-sm border border-slate-100" data-id="element-63">
                  <span className="text-3xl" aria-hidden="true" data-id="element-64">
                    📚
                  </span>
                  <h2 className="text-2xl font-bold text-slate-900" data-id="element-65">
                    Cito Training
                  </h2>
                </div>
                <div className="flex-1 h-px bg-gradient-to-r from-indigo-200 to-transparent" aria-hidden="true" data-id="element-66"></div>
              </div>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.1
            }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 auto-rows-fr" data-id="element-67">
                {MODULES.map((module, index) => {
                const moduleProgress = state.progress[module.id] || {
                  masteryPercentage: 0,
                  status: 'learning' as const,
                  cheatSheetViewed: false,
                  questionsAnswered: [],
                  recentQuestions: [],
                  reviewNeededCount: 0,
                  uncertainCount: 0,
                  overconfidentCount: 0
                };
                return <motion.div key={module.id} initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 0.2 + index * 0.1
                }} className="h-full" data-id="element-68">
                      <ModuleCard module={module} progress={moduleProgress} onClick={() => handleModuleClick(module.id)} onCheatSheetClick={() => handleCheatSheetClick(module.id)} data-id="element-69" />
                    </motion.div>;
              })}
              </motion.div>
            </div>

            {/* BONUS FEATURES */}
            <div className="mb-12" data-id="element-70">
              <div className="flex items-center gap-4 mb-8" data-id="element-71">
                <div className="flex items-center gap-3 bg-white px-6 py-3 rounded-2xl shadow-sm border border-slate-100" data-id="element-72">
                  <span className="text-3xl" aria-hidden="true" data-id="element-73">
                    ⭐
                  </span>
                  <h2 className="text-2xl font-bold text-slate-900" data-id="element-74">
                    Bonus Features
                  </h2>
                </div>
                <div className="flex-1 h-px bg-gradient-to-r from-amber-200 to-transparent" aria-hidden="true" data-id="element-75"></div>
              </div>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.3
            }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 auto-rows-fr" data-id="element-76">
                {unlockedBonusFeatures.map((feature, index) => <motion.div key={feature.id} initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.4 + index * 0.1
              }} whileHover={!feature.isLocked && !feature.comingSoon ? {
                scale: 1.02,
                y: -5
              } : {}} whileTap={!feature.isLocked && !feature.comingSoon ? {
                scale: 0.98
              } : {}} className="relative h-full" data-id="element-77">
                    <Card className={`p-8 h-full flex flex-col ${feature.isLocked || feature.comingSoon ? 'opacity-80 cursor-not-allowed bg-slate-50' : 'cursor-pointer hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-indigo-100'}`} onClick={() => handleBonusFeatureClick(feature.id)} role={!feature.isLocked && !feature.comingSoon ? 'button' : undefined} tabIndex={!feature.isLocked && !feature.comingSoon ? 0 : undefined} onKeyDown={e => {
                  if (!feature.isLocked && !feature.comingSoon && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    handleBonusFeatureClick(feature.id);
                  }
                }} aria-label={feature.isLocked ? `${feature.title}, vergrendeld tot level ${feature.unlockLevel}` : feature.comingSoon ? `${feature.title}, binnenkort beschikbaar` : `${feature.title}, klik om te starten`} data-id="element-78">
                      {feature.isLocked && <div className="absolute top-4 right-4 bg-slate-200 p-2 rounded-full" aria-hidden="true" data-id="element-79">
                          <Lock className="w-5 h-5 text-slate-500" data-id="element-80" />
                        </div>}
                      {feature.comingSoon && !feature.isLocked && <div className="absolute top-4 right-4 bg-amber-100 p-2 rounded-full" aria-hidden="true" data-id="element-81">
                          <Clock className="w-5 h-5 text-amber-600" data-id="element-82" />
                        </div>}

                      <div className="flex-1" data-id="element-83">
                        <div className={`w-16 h-16 rounded-2xl ${feature.isLocked ? 'bg-slate-200' : feature.comingSoon ? 'bg-amber-50' : 'bg-indigo-50'} flex items-center justify-center mb-6 shadow-sm`} aria-hidden="true" data-id="element-84">
                          <span className="text-3xl" data-id="element-85">
                            {feature.id === 'CitoSimulator' && '🎯'}
                            {feature.id === 'SnelLezen' && '⚡'}
                            {feature.id === 'Studievaardigheden' && '📖'}
                            {feature.id === 'ExpertModus' && '🏆'}
                          </span>
                        </div>

                        <h3 className="font-bold text-slate-900 mb-3 text-lg" data-id="element-86">
                          {feature.title}
                        </h3>
                        <p className="text-sm text-slate-600 mb-6 leading-relaxed" data-id="element-87">
                          {feature.description}
                        </p>
                      </div>

                      <div className={`text-xs font-bold uppercase tracking-wider py-2 px-3 rounded-lg inline-flex items-center gap-2 mt-auto ${feature.isLocked ? 'bg-slate-200 text-slate-500' : feature.comingSoon ? 'bg-amber-100 text-amber-700' : 'bg-indigo-50 text-indigo-600'}`} data-id="element-88">
                        {feature.isLocked ? <>
                            <Lock className="w-3 h-3" aria-hidden="true" data-id="element-89" />
                            Unlock op Level {feature.unlockLevel}
                          </> : feature.comingSoon ? <>
                            <Clock className="w-3 h-3" aria-hidden="true" data-id="element-90" />
                            Binnenkort
                          </> : <>
                            <Sparkles className="w-3 h-3" aria-hidden="true" data-id="element-91" />
                            Unlocked!
                          </>}
                      </div>
                    </Card>
                  </motion.div>)}
              </motion.div>
            </div>
          </main>
        </div>

        {/* Cheat Sheet Drawer from Dashboard */}
        {cheatSheetModuleId && <CheatSheetDrawer isOpen={true} onClose={() => {
        markCheatSheetViewed(cheatSheetModuleId);
        setCheatSheetModuleId(null);
      }} moduleId={cheatSheetModuleId} data-id="element-92" />}

        {/* Global Error Log Panel */}
        <ErrorLogPanel state={state} isOpen={showErrorLog} onClose={() => setShowErrorLog(false)} data-id="element-93" />

        {/* Global Level Up Modal */}
        {state.pendingLevelUp && <LevelUpModal isOpen={true} level={state.pendingLevelUp} xp={state.stats.xp} onClose={clearPendingLevelUp} data-id="element-94" />}
      </div>
    </>;
}